import { useEffect, useRef, useState } from 'react';
import { useTransition } from '@/hooks/useTransition';
import generateTimeStamp from '@/utils/generateTimeStamp';
import { getSizesByAdType, AllAdOptions, getDevicesByAdType } from '@/lib/ads';
import useBreakpoint from '@/hooks/useBreakpoint';
import classNames from '@/utils/className';
import useScriptFlag from '@/hooks/useScriptFlag';
// @refresh reset

interface PropsFromAd {
  type: AllAdOptions;
  marginBottomP?: string;
  idCountPosition?: string;
}

<div className="desktop:m-0" />;

const variants = {
  allDevices: {
    true: 'flex self-center justify-self-center',
    false: '',
  },
  desktopOnly: {
    true: 'hidden p-0 m-0 desktop:flex desktop:self-center desktop:justify-self-center',
    false: '',
  },
  mobileOnly: {
    true: 'flex self-center justify-self-center desktop:hidden desktop:p-0 desktop:m-0 [grid-column-start:1] [grid-column-end:2] tablet:[grid-column-start:initial] tablet:[grid-column-end:initial]',
    false: '',
  },
  rectangle: {
    true: 'items-center justify-center bg-black/5 w-[336px] h-[300px]',
    false: '',
  },
  halfpage: {
    true: 'items-center justify-center bg-black/5 w-[336px] min-h-[300px]',
    false: '',
  },
  manualFlyingCarpet: {
    true: 'manual-flying-carpet',
    false: '',
  },
  billboard: {
    true: 'hidden p-0 m-0 desktop:flex desktop:justify-center desktop:items-center desktop:my-6 desktop:mx-auto desktop:bg-black/5 desktop:w-[970px] desktop:h-[250px]',
    false: '',
  },
} as const;

export default function AdsTypeGenerators({ type, idCountPosition = '', marginBottomP = '0px' }: PropsFromAd) {
  const { isTransitioning } = useTransition();
  const [isTabletOrMobile] = useBreakpoint('tablet');
  const shouldRenderAds = useScriptFlag({ adFlag: 'google' });
  const [hasScrolled, setIsScrolled] = useState(false);

  const idTimeStampUnique = generateTimeStamp();
  const tag = useRef(`div-gpt-ad-${type}-${idCountPosition !== '' ? `${idCountPosition}-${idTimeStampUnique}` : idTimeStampUnique}`);

  const { mobile, desktop } = getDevicesByAdType(type);
  const isNotAnchor = type !== 'anchor' && type !== 'Anchor_Footer';

  if (type === 'anchor' || type === 'Anchor_Footer') {
    document.addEventListener('scroll', () => {
      if (hasScrolled) {
        return;
      }
      setIsScrolled(true);
    });
  }

  useEffect(() => {
    const isTargetAd = type.includes('rectangle') || type.includes('billboard');

    if (isTargetAd) {
      const { adContainerReadyFired, dataLayer } = (window as any) || [];
      if (!adContainerReadyFired) {
        dataLayer.push({ event: 'ad_container_ready' });
        (window as any).adContainerReadyFired = true;
      }
    }
  }, [type]);

  useEffect(() => {
    if ((type === 'Anchor_Footer' && isTabletOrMobile) || (type === 'anchor' && isTabletOrMobile === false)) return;

    if ((mobile && isTabletOrMobile) || (desktop && !isTabletOrMobile)) {
      if (isTransitioning || typeof window === undefined || !shouldRenderAds) {
        return;
      }

      if (type === 'billboard/main/home' || type === 'rectangle/main/internal' || type === 'halfpage') {
        return;
      }
    }
  }, [type, idCountPosition, isTabletOrMobile, isTransitioning, hasScrolled, mobile, desktop, shouldRenderAds]);

  if (isNotAnchor) {
    return (
      <div
        id={tag.current}
        style={{
          marginBottom: marginBottomP,
        }}
        data-ad="true"
        className={classNames(
          variants.manualFlyingCarpet[(!!getSizesByAdType(type).find(sizes => sizes[1] > 300) && type !== 'halfpage').toString()],
          variants.allDevices[(mobile && desktop).toString()],
          variants.mobileOnly[(mobile && !desktop).toString()],
          variants.desktopOnly[(desktop && !mobile).toString()],
          variants.billboard[type.includes('billboard').toString()],
          variants.rectangle[type.includes('rectangle').toString()],
          variants.halfpage[(type === 'halfpage').toString()],
          `${type}${idCountPosition !== '' ? `-${idCountPosition} ` : ''}`
        )}
      />
    );
  }
  if (!isNotAnchor) {
    if ((type === 'Anchor_Footer' && isTabletOrMobile) || (type === 'anchor' && isTabletOrMobile === false)) return <></>;

    return (
      <div
        className={classNames(
          'fixed bottom-0 z-[999] flex  w-full flex-row items-center justify-center border bg-mglGray50 bg-opacity-20 backdrop-blur-sm tablet:w-[calc(100%_-_60px)]',
          hasScrolled ? 'h-[50px] tablet:h-[90px]' : ''
        )}>
        <div id={tag.current} className={classNames(type, 'flex flex-row items-center justify-center')} data-ad="true" />
      </div>
    );
  }
  return <></>;
}
